import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import ScrollReveal from "@/components/ScrollReveal";
import { Shield, Lock, Eye, Database, UserCheck, Mail, Globe, FileText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";

const sections = [
  {
    icon: Database,
    title: "Information We Collect",
    content: [
      "Personal information you provide directly, such as your name, email address, phone number, and business name when you fill out our contact forms, book a consultation, or subscribe to our newsletter.",
      "Usage data collected automatically, including your IP address, browser type, device information, pages visited, time spent on pages, and referring URLs.",
      "Cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and personalize content.",
      "Communication data from interactions with our AI chatbot, including conversation logs used to improve service quality and train our AI models.",
    ],
  },
  {
    icon: Eye,
    title: "How We Use Your Information",
    content: [
      "To provide, maintain, and improve our AI chatbot automation services and other digital solutions.",
      "To process your inquiries, schedule consultations, and communicate with you about our services.",
      "To send you marketing communications, newsletters, and updates about our services (with your consent).",
      "To analyze website usage patterns and optimize our website performance and user experience.",
      "To comply with legal obligations and protect our rights, privacy, safety, and property.",
    ],
  },
  {
    icon: UserCheck,
    title: "Information Sharing",
    content: [
      "We do not sell, trade, or rent your personal information to third parties.",
      "We may share information with trusted service providers who assist us in operating our website, conducting business, or servicing you, provided they agree to keep this information confidential.",
      "We may disclose information when required by law, to enforce our site policies, or to protect our or others' rights, property, or safety.",
      "In the event of a business transfer, merger, or acquisition, your information may be transferred as part of the business assets.",
    ],
  },
  {
    icon: Lock,
    title: "Data Security",
    content: [
      "We implement industry-standard security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction.",
      "All data transmissions are encrypted using SSL/TLS technology.",
      "Access to personal information is restricted to authorized personnel who need it to perform their job functions.",
      "We regularly review and update our security practices to ensure your data remains protected.",
    ],
  },
  {
    icon: Globe,
    title: "Cookies & Tracking",
    content: [
      "We use essential cookies necessary for website functionality, such as session management and security.",
      "Analytics cookies help us understand how visitors interact with our website, allowing us to improve our content and user experience.",
      "You can control cookie preferences through your browser settings. Disabling certain cookies may affect website functionality.",
      "We may use third-party analytics services like Google Analytics, which have their own privacy policies governing their use of your information.",
    ],
  },
  {
    icon: Shield,
    title: "Your Rights",
    content: [
      "You have the right to access, correct, or delete your personal information at any time by contacting us.",
      "You may opt out of receiving marketing communications by clicking the unsubscribe link in any email or contacting us directly.",
      "You have the right to request a copy of the personal data we hold about you.",
      "California residents may have additional rights under the CCPA, including the right to know what personal information is collected and how it is used.",
    ],
  },
  {
    icon: FileText,
    title: "Third-Party Links",
    content: [
      "Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites.",
      "We encourage you to read the privacy policies of any third-party websites you visit.",
      "Our AI chatbot integrations with platforms like WhatsApp, Facebook Messenger, and others are subject to those platforms' respective privacy policies.",
    ],
  },
  {
    icon: Mail,
    title: "Contact Us",
    content: [
      "If you have any questions or concerns about this Privacy Policy, please contact us at info@xenticsolutions.com.",
      "You may also reach us by phone at +1 (872) 297-0607 or visit us at 107 Williamsburg Lane, Bolingbrook, IL 60440.",
      "We will respond to your inquiry within 30 business days.",
    ],
  },
];

const PrivacyPolicy = () => (
  <Layout>
    <SEOHead
      title="Privacy Policy | Xentic Solutions"
      description="Learn how Xentic Solutions collects, uses, and protects your personal information. We are committed to safeguarding your privacy."
      path="/privacy-policy"
    />

    {/* Hero */}
    <section className="py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 grid-overlay" />
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-blob" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/20 rounded-full blur-[120px] animate-blob-delay" />
      <div className="container relative z-10 text-center">
        <ScrollReveal>
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-6 border border-primary/20">
            <Shield className="h-4 w-4" />
            Your Privacy Matters
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
            Privacy <span className="text-gradient">Policy</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            At Xentic Solutions, we take your privacy seriously. This policy explains how we collect, use, and protect your personal information.
          </p>
          <p className="text-sm text-muted-foreground/60 mt-4">
            Last updated: March 1, 2026
          </p>
        </ScrollReveal>
      </div>
    </section>

    {/* Content */}
    <section className="py-16">
      <div className="container max-w-4xl space-y-8">
        {sections.map((section, i) => (
          <ScrollReveal key={i} delay={i * 0.05}>
            <motion.div whileHover={{ scale: 1.005 }} transition={{ type: "spring", stiffness: 400, damping: 30 }}>
              <Card className="overflow-hidden group hover:border-primary/30 transition-colors duration-300">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors duration-300">
                      <section.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-heading text-xl md:text-2xl font-bold mb-4">{section.title}</h2>
                      <ul className="space-y-3">
                        {section.content.map((item, j) => (
                          <li key={j} className="flex items-start gap-3 text-sm text-muted-foreground leading-relaxed">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </ScrollReveal>
        ))}
      </div>
    </section>

    {/* Bottom Note */}
    <section className="py-12 section-alt">
      <div className="container max-w-2xl text-center">
        <ScrollReveal>
          <p className="text-sm text-muted-foreground leading-relaxed">
            By using our website and services, you acknowledge that you have read and understood this Privacy Policy. We reserve the right to update this policy at any time. Changes will be posted on this page with an updated revision date.
          </p>
        </ScrollReveal>
      </div>
    </section>
  </Layout>
);

export default PrivacyPolicy;
